import React, { createContext, useContext, useEffect, useState } from "react";
import { api, ApiClientError, setAuthToken, removeAuthToken, User } from "@/lib/api";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem("auth_token");
    if (token) {
      api.auth
        .getCurrentUser()
        .then(setUser)
        .catch(() => {
          // Token invalid, remove it
          removeAuthToken();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await api.auth.login(email, password);
      setAuthToken(response.access_token);
      const currentUser = await api.auth.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      if (error instanceof ApiClientError) {
        throw new Error(error.detail || "Login failed");
      }
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Call backend logout endpoint to invalidate token
      await api.auth.logout();
    } catch (error) {
      // Ignore errors - we'll still clear local state
    } finally {
      removeAuthToken();
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}



